import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Plus, Trash2, Clock, Inbox as InboxIcon, Sparkles, ChevronDown, ChevronRight, Pencil } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import PageHeader from '../../components/PageHeader';
import { toast } from 'sonner';
import api, { apiError, fmtDate } from '../../lib/api';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// Day codes are stable identifiers shared with the backend / availability submissions —
// keep them in English. Display them translated via DAY_LABELS.
const DAY_LABELS = { Mon: 'Lun', Tue: 'Mar', Wed: 'Mer', Thu: 'Jeu', Fri: 'Ven', Sat: 'Sam', Sun: 'Dim' };

// Heures d'un créneau (« 11:00 – 19:00 », « 11h-19h », « 8h30-17h »…), lues comme le serveur (_heures_creneau) :
// ['11:00', '19:00']. Un créneau qui n'a pas exactement 2 heures valides est ignoré par la rotation du bot d'accueil (#21).
function heuresDuCreneau(t) {
  const s = String(t || '');
  const re = /(\d{1,2})\s*(?:[hH]\s?|:)(\d{2})?(?!\d)/g;
  const out = [];
  let m;
  while ((m = re.exec(s))) {
    if (m.index > 0 && /[\d:]/.test(s[m.index - 1])) continue;   // milieu d'un nombre ou secondes (« 11:00:00 »)
    const h = Number(m[1]);
    const mn = Number(m[2] || 0);
    out.push(h > 24 || mn > 59 || h * 60 + mn > 1440 ? null : `${String(h).padStart(2, '0')}:${String(mn).padStart(2, '0')}`);
  }
  return out;
}
const creneauLisible = (t) => { const h = heuresDuCreneau(t); return h.length === 2 && h.every(Boolean); };

// Suivi « dimanche obligatoire » : qui a choisi son dimanche du mois, qui traîne.
function SundayDutyCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/planning/sunday-duty/all').then(({ data }) => setData(data)).catch(() => {});
  }, []);
  if (!data || !(data.chatters || []).length) return null;
  const fmt = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  const manquants = data.chatters.filter(c => !c.choix);
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 mb-4">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-sm font-semibold text-white">📅 Dimanche du mois ({data.mois})</span>
        <span className="text-[11px] text-slate-500">chatteurs sans dimanche au planning de base — choix obligatoire, ajouté au planning automatiquement</span>
        {manquants.length > 0 && <span className="ml-auto text-[11px] text-amber-300 font-semibold">{manquants.length} n'ont pas encore choisi</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        {data.chatters.map(c => (
          <span key={c.chatter_id} className={`text-[12px] px-2.5 py-1 rounded-lg border ${c.choix ? 'border-emerald-500/25 bg-emerald-500/[0.07] text-emerald-200' : 'border-amber-500/30 bg-amber-500/[0.07] text-amber-200'}`}>
            {c.name} : {c.choix ? <>dim. {fmt(c.choix.date)} · {c.choix.moment === 'soir' ? '🌙 soir' : '☀️ jour'}{c.choix.planned ? ' ✓ planifié' : ''}</> : 'pas encore choisi (bloqué dans l’app)'}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function ManagePlanning({ embedded = false }) {
  const [shifts, setShifts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [chatters, setChatters] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [week, setWeek] = useState('current');
  const [editCell, setEditCell] = useState(null); // {day, shift}
  const [shiftDlg, setShiftDlg] = useState({ open: false, shift: null }); // shift=null → create
  const [addEmp, setAddEmp] = useState(false); // person-first "add employee to the rota" dialog

  const load = async (w = week) => {
    try {
      const [s, a, c] = await Promise.all([
        api.get('/planning/shifts'),
        api.get('/planning/assignments', { params: { week: w } }),
        api.get('/chatters'),
      ]);
      setShifts(s.data);
      setAssignments(a.data);
      setChatters(c.data);
    } catch (e) { toast.error(apiError(e, 'Échec du chargement du planning')); }
    api.get('/planning/availability').then(({ data }) => setSubmissions(data)).catch(() => {});
  };
  useEffect(() => { load(week); }, [week]);

  const cell = (day, shiftId) => assignments.find(x => x.day === day && x.shift_id === shiftId);

  const delShift = async (id) => {
    try {
      await api.delete(`/planning/shifts/${id}`);
      setShifts(shifts.filter(x => x.id !== id));
      toast.success('Shift supprimé');
    } catch (e) { toast.error(apiError(e, 'Échec de la suppression')); }
  };

  const [applying, setApplying] = useState(false);
  const applyAvailability = async () => {
    setApplying(true);
    try {
      const { data } = await api.post('/planning/availability/apply', null, { params: { week: 'next' } });
      toast.success(`Planning pré-rempli — ${data.filled_slots} créneau(x) ajoutés depuis les dispos`);
      load();
    } catch (e) { toast.error(apiError(e, 'Pré-remplissage échoué')); } finally { setApplying(false); }
  };

  const shiftLabel = (id) => (shifts.find(s => s.id === id) || {}).label || 'Shift';
  // Group a submission's "day|shiftId" slots into { day: [labels] } for readable display.
  const groupSlots = (slotList) => {
    const by = {};
    (slotList || []).forEach(k => { const [d, sid] = String(k).split('|'); (by[d] = by[d] || []).push(shiftLabel(sid)); });
    return DAYS.filter(d => by[d]).map(d => ({ day: d, labels: by[d] }));
  };

  const wrapperCls = embedded ? 'fade-in' : 'p-6 md:p-8 max-w-7xl mx-auto fade-in';

  return (
    <div className={wrapperCls} data-testid="manage-planning-page">
      {!embedded && (
        <PageHeader
          title="Gérer le planning"
          subtitle="Shifts, assignations hebdomadaires et disponibilités soumises par les employés"
          actions={<div className="flex gap-2">
            <Button data-testid="add-employee-planning-button" onClick={() => setAddEmp(true)} className="bg-emerald-500 hover:bg-emerald-600"><Plus size={16} className="mr-1.5" /> Ajouter un employé</Button>
            <Button data-testid="add-shift-button" variant="outline" onClick={() => setShiftDlg({ open: true, shift: null })} className="border-white/15 bg-white/5 text-slate-200 hover:bg-white/10"><Clock size={15} className="mr-1.5" /> Ajouter un shift</Button>
          </div>}
          onRefresh={() => load()}
        />
      )}
      {embedded && (
        <div className="flex justify-end gap-2 mb-3">
          <Button data-testid="add-employee-planning-button" onClick={() => setAddEmp(true)} className="bg-emerald-500 hover:bg-emerald-600"><Plus size={16} className="mr-1.5" /> Ajouter un employé</Button>
          <Button data-testid="add-shift-button" variant="outline" onClick={() => setShiftDlg({ open: true, shift: null })} className="border-white/15 bg-white/5 text-slate-200 hover:bg-white/10"><Clock size={15} className="mr-1.5" /> Ajouter un shift</Button>
        </div>
      )}

      <div className="flex items-center gap-2 mb-4 text-[12px] text-slate-400 rounded-xl border border-indigo-500/15 bg-indigo-500/[0.04] px-4 py-2.5">
        <Clock size={14} className="text-indigo-400 shrink-0" />
        <span>Planning <span className="text-white font-medium">récurrent</span> — tu le mets une fois et il s'applique <span className="text-white">chaque semaine</span>. Les ajustements ponctuels se font à la main.</span>
      </div>

      <SundayDutyCard />

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px]">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
                <th className="px-4 py-2.5 text-left font-semibold">Shift</th>
                {DAYS.map(d => <th key={d} className="px-3 py-2.5 font-semibold text-center">{DAY_LABELS[d] || d}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {shifts.map(s => (
                <tr key={s.id}>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-3 h-3 rounded-full ${s.color}`} />
                      <div>
                        <div className="text-sm text-white font-semibold">{s.label}</div>
                        <div className="text-[11px] text-slate-400">{s.time || '—'}</div>
                      </div>
                    </div>
                  </td>
                  {DAYS.map(d => {
                    const a = cell(d, s.id);
                    const count = a?.chatter_names?.length || 0;
                    return (
                      <td key={d} className="px-2 py-2 text-center align-top">
                        <button data-testid={`assign-cell-${d}-${(s.label || '').toLowerCase().replace(/\s+/g, '-')}`} onClick={() => setEditCell({ day: d, shift: s })} className={`w-full min-h-[64px] rounded-lg border p-1.5 space-y-1 transition-colors ${count ? 'border-white/10 bg-white/[0.03] hover:border-indigo-500/50' : 'border-dashed border-white/10 hover:border-indigo-500/40'}`}>
                          {(a?.chatter_names || []).map(n => (
                            <div key={n} className="text-[11px] px-1.5 py-1 rounded-md bg-indigo-500/15 border border-indigo-500/25 text-indigo-200 truncate">{n}</div>
                          ))}
                          {count === 0 && <span className="text-[11px] text-slate-600">+ assigner</span>}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <div className="px-5 py-3 border-b border-white/5 flex items-center gap-2 text-sm font-semibold text-white"><Clock size={14} className="text-indigo-400" /> Shifts</div>
          <div className="divide-y divide-white/5">
            {shifts.map(s => (
              <div key={s.id} className="px-5 py-3 flex items-center gap-3" data-testid={`shift-row-${s.id}`}>
                <div className={`w-3 h-3 rounded-full ${s.color}`} />
                <div className="flex-1">
                  <div className="text-sm text-white font-medium">{s.label}</div>
                  <div className="text-[11px] text-slate-500">
                    {s.time || 'Pas d\'horaire'}
                    {s.time && !creneauLisible(s.time) && (
                      <span className="ml-1.5 text-amber-300" data-testid={`shift-horaire-incomplet-${s.id}`} title="Mets l'heure de début et l'heure de fin">⚠ horaire incomplet</span>
                    )}
                  </div>
                </div>
                <Button data-testid={`edit-shift-${s.id}`} variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-white hover:bg-white/10" onClick={() => setShiftDlg({ open: true, shift: s })}><Pencil size={13} /></Button>
                <Button data-testid={`delete-shift-${s.id}`} variant="ghost" size="icon" className="h-7 w-7 text-rose-400 hover:bg-rose-500/10" onClick={() => delShift(s.id)}><Trash2 size={13} /></Button>
              </div>
            ))}
          </div>
        </div>

      </div>
      {/* Bloc « Disponibilités soumises » RETIRÉ le 02/09 (Marlon) : le planning est une
          grille fixe récurrente — seul le choix du dimanche reste (carte ci-dessus). */}

      <AssignDialog
        cell={editCell}
        week={week}
        chatters={chatters}
        current={editCell ? cell(editCell.day, editCell.shift.id) : null}
        assignments={assignments}
        onClose={() => setEditCell(null)}
        onSaved={() => { setEditCell(null); load(); }}
      />

      <ShiftDialog
        open={shiftDlg.open}
        shift={shiftDlg.shift}
        onOpenChange={(v) => setShiftDlg(s => ({ ...s, open: v }))}
        onDone={() => load()}
      />

      <AddEmployeeDialog
        open={addEmp}
        week={week}
        shifts={shifts}
        chatters={chatters}
        assignments={assignments}
        onClose={() => setAddEmp(false)}
        onSaved={() => { setAddEmp(false); load(); }}
      />
    </div>
  );
}

function AssignDialog({ cell, week, chatters, current, assignments = [], onClose, onSaved }) {
  const [selected, setSelected] = useState([]);
  const [allWeek, setAllWeek] = useState(false);
  const [restDay, setRestDay] = useState(null); // day code (Mon..Sun) — the chatter's weekly day off
  const [saving, setSaving] = useState(false);

  useEffect(() => { setSelected(current?.chatter_ids || []); setAllWeek(false); setRestDay(null); }, [cell]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      if (!allWeek) {
        await api.post('/planning/assignments', { week, day: cell.day, shift_id: cell.shift.id, chatter_ids: selected });
      } else {
        // Week mode: spread only the NEWLY-checked people (the delta) across the
        // week's same shift — never the cell's pre-existing team, and never on the
        // chosen rest day. Each day keeps its own team (merge, no wipe).
        const original = current?.chatter_ids || [];
        const added = selected.filter(id => !original.includes(id));
        for (const d of DAYS) {
          const base = d === cell.day
            ? selected
            : (assignments.find(a => a.day === d && a.shift_id === cell.shift.id)?.chatter_ids || []);
          const final = d === restDay
            ? base.filter(id => !added.includes(id))            // day off → not scheduled
            : (d === cell.day ? base : [...new Set([...base, ...added])]);
          await api.post('/planning/assignments', { week, day: d, shift_id: cell.shift.id, chatter_ids: final });
        }
      }
      toast.success(allWeek
        ? (restDay ? `Appliqué 6 j/7 — repos le ${DAY_LABELS[restDay]}` : 'Appliqué à toute la semaine (7 j/7)')
        : 'Assignation enregistrée');
      onSaved();
    } catch (e) { toast.error(apiError(e, "Échec de l'enregistrement")); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!cell} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200">
        <DialogHeader><DialogTitle className="text-white">{cell ? `${DAY_LABELS[cell.day] || cell.day} — ${cell.shift.label}` : ''}</DialogTitle></DialogHeader>
        <div className="space-y-1.5 pt-2 max-h-[50vh] overflow-y-auto scrollbar-thin">
          {chatters.map(c => (
            <label key={c.id} className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-white/5 cursor-pointer">
              <Checkbox data-testid={`assign-chatter-${c.id}`} checked={selected.includes(c.id)} onCheckedChange={() => setSelected(selected.includes(c.id) ? selected.filter(x => x !== c.id) : [...selected, c.id])} />
              <span className="text-sm text-white">{c.name}</span>
              <span className="text-[10px] text-slate-500 ml-auto">{c.role}</span>
            </label>
          ))}
        </div>
        <div className="rounded-lg bg-indigo-500/10 border border-indigo-500/20">
          <label className="flex items-center gap-3 px-3 py-2.5 cursor-pointer">
            <Checkbox data-testid="assign-all-week-checkbox" checked={allWeek} onCheckedChange={() => setAllWeek(v => !v)} />
            <span className="text-sm text-indigo-200">Appliquer à toute la semaine <span className="text-slate-400">(ajoute les nouveaux cochés au shift « {cell?.shift?.label} » chaque jour, sans retirer personne ailleurs)</span></span>
          </label>
          {allWeek && (
            <div className="px-3 pb-3">
              <div className="text-[11px] text-slate-400 mb-1.5">Jour de repos <span className="text-slate-500">(il ne sera pas planifié ce jour-là)</span> :</div>
              <div className="flex flex-wrap gap-1.5">
                {DAYS.map(d => (
                  <button key={d} type="button" data-testid={`rest-day-${d}`} onClick={() => setRestDay(r => r === d ? null : d)}
                    className={`px-2.5 py-1 rounded-full text-[12px] border transition-colors ${restDay === d ? 'bg-amber-500/20 border-amber-500/50 text-amber-300 font-semibold' : 'bg-white/5 border-white/10 text-slate-300 hover:border-white/25'}`}>
                    {restDay === d ? '😴 ' : ''}{DAY_LABELS[d]}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <Button data-testid="assign-save-button" className="w-full bg-indigo-500 hover:bg-indigo-600" onClick={save} disabled={saving}>{saving ? 'Enregistrement...' : allWeek ? `Assigner ${selected.length} employé${selected.length > 1 ? 's' : ''} — 7 jours` : `Assigner ${selected.length} employé${selected.length > 1 ? 's' : ''}`}</Button>
      </DialogContent>
    </Dialog>
  );
}

// Person-first: add ONE employee to a shift for the whole week (minus their day off),
// in a single dialog — no need to open day-by-day cells. Merges into each day's
// existing team (never wipes anyone).
function AddEmployeeDialog({ open, week, shifts, chatters, assignments = [], onClose, onSaved }) {
  const [empId, setEmpId] = useState('');
  const [shiftId, setShiftId] = useState('');
  const [restDay, setRestDay] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setEmpId('');
      setShiftId(shifts[0]?.id || '');
      setRestDay(null);
    }
  }, [open]); // eslint-disable-line

  const emp = chatters.find(c => c.id === empId);
  const shift = shifts.find(s => s.id === shiftId);

  const save = async () => {
    if (!empId || !shiftId) { toast.error('Choisis un employé et un créneau'); return; }
    setSaving(true);
    try {
      for (const d of DAYS) {
        const existing = assignments.find(a => a.day === d && a.shift_id === shiftId)?.chatter_ids || [];
        const ids = d === restDay
          ? existing.filter(id => id !== empId)          // day off → make sure he's NOT there
          : [...new Set([...existing, empId])];          // working day → add him, keep the rest
        await api.post('/planning/assignments', { week, day: d, shift_id: shiftId, chatter_ids: ids });
      }
      toast.success(restDay
        ? `${emp?.name} ajouté au shift « ${shift?.label} » — 6 j/7, repos le ${DAY_LABELS[restDay]}`
        : `${emp?.name} ajouté au shift « ${shift?.label} » — 7 j/7`);
      onSaved();
    } catch (e) { toast.error(apiError(e, "Échec de l'ajout")); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200">
        <DialogHeader><DialogTitle className="text-white">Ajouter un employé au planning</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">1. Employé</Label>
            <select data-testid="add-emp-select" value={empId} onChange={e => setEmpId(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white h-10 outline-none focus:border-emerald-500/50">
              <option value="" className="bg-[#0B0B0E]">— Choisir —</option>
              {chatters.filter(c => !String(c.id).startsWith('owner-')).map(c => (
                <option key={c.id} value={c.id} className="bg-[#0B0B0E]">{c.name} ({c.role})</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">2. Créneau (shift)</Label>
            <select data-testid="add-emp-shift-select" value={shiftId} onChange={e => setShiftId(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white h-10 outline-none focus:border-emerald-500/50">
              {shifts.map(s => <option key={s.id} value={s.id} className="bg-[#0B0B0E]">{s.label}{s.time ? ` (${s.time})` : ''}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">3. Jour de repos <span className="text-slate-500">(optionnel — sinon 7 j/7)</span></Label>
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map(d => (
                <button key={d} type="button" data-testid={`add-emp-rest-${d}`} onClick={() => setRestDay(r => r === d ? null : d)}
                  className={`px-2.5 py-1 rounded-full text-[12px] border transition-colors ${restDay === d ? 'bg-amber-500/20 border-amber-500/50 text-amber-300 font-semibold' : 'bg-white/5 border-white/10 text-slate-300 hover:border-white/25'}`}>
                  {restDay === d ? '😴 ' : ''}{DAY_LABELS[d]}
                </button>
              ))}
            </div>
          </div>
          {empId && shiftId && (
            <div className="text-[12px] text-emerald-300/90 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">
              {emp?.name} sera sur « {shift?.label} » {restDay ? `du ${DAY_LABELS[DAYS.filter(d => d !== restDay)[0]]} au ${DAY_LABELS[[...DAYS].filter(d => d !== restDay).slice(-1)[0]]}, repos le ${DAY_LABELS[restDay]} (6 j/7)` : 'tous les jours (7 j/7)'}.
            </div>
          )}
          <Button data-testid="add-emp-save-button" className="w-full bg-emerald-500 hover:bg-emerald-600" onClick={save} disabled={saving || !empId}>{saving ? 'Ajout...' : 'Ajouter au planning'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SubmissionRow({ s, groups }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`submission-row-${s.id}`}>
      <button onClick={() => setOpen(o => !o)} className="w-full px-5 py-3 flex items-center gap-3 hover:bg-white/[0.02] text-left">
        {open ? <ChevronDown size={14} className="text-slate-500 shrink-0" /> : <ChevronRight size={14} className="text-slate-500 shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white font-medium truncate">{s.user_name}</div>
          <div className="text-[11px] text-slate-500">Soumis {fmtDate(s.created_at)}</div>
        </div>
        <Badge className="bg-indigo-500/15 text-indigo-300 border-indigo-500/30">{s.slots.length} créneaux</Badge>
      </button>
      {open && (
        <div className="px-5 pb-3 pl-12 space-y-1.5">
          {groups.length === 0 ? <div className="text-[11px] text-slate-600">Aucun créneau.</div> : groups.map(g => (
            <div key={g.day} className="flex items-start gap-2 text-[12px]">
              <span className="w-9 shrink-0 text-slate-500 font-medium">{DAY_LABELS[g.day] || g.day}</span>
              <div className="flex flex-wrap gap-1">
                {g.labels.map((l, i) => <span key={i} className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-slate-300">{l}</span>)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const COLORS = ['bg-amber-500', 'bg-indigo-500', 'bg-emerald-500', 'bg-rose-500', 'bg-sky-500', 'bg-violet-500', 'bg-orange-500'];

function ShiftDialog({ shift, open, onOpenChange, onDone }) {
  const [label, setLabel] = useState('');
  const [start, setStart] = useState('11:00');
  const [end, setEnd] = useState('19:00');
  const [color, setColor] = useState('bg-indigo-500');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLabel(shift?.label || '');
    // Horaire existant repris tel quel : une heure manquante reste vide (plus de « 19:00 » inventé, #21).
    const heures = heuresDuCreneau(shift?.time).filter(Boolean);
    if (heures.length) { setStart(heures[0] || ''); setEnd(heures[1] || ''); } else { setStart('11:00'); setEnd('19:00'); }
    setColor(shift?.color || 'bg-indigo-500');
  }, [open, shift]);

  const save = async () => {
    if (!label.trim()) { toast.error('Donne un nom au shift'); return; }
    // #21 : une seule heure = créneau illisible (chatteurs « hors shift » pour le bot d'accueil) → refusé.
    if (!start !== !end) { toast.error("Mets l'heure de début et l'heure de fin."); return; }
    setSaving(true);
    try {
      const time = (start && end) ? `${start} – ${end}` : '';
      if (shift) await api.patch(`/planning/shifts/${shift.id}`, { label: label.trim(), time, color });
      else await api.post('/planning/shifts', { label: label.trim(), time, color });
      onOpenChange(false);
      toast.success(shift ? 'Shift modifié' : 'Shift créé');
      onDone();
    } catch (e) { toast.error(apiError(e, 'Échec')); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200">
        <DialogHeader><DialogTitle className="text-white">{shift ? 'Modifier le shift' : 'Créer un shift'}</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Nom du shift</Label><Input data-testid="shift-label-input" value={label} onChange={e => setLabel(e.target.value)} className="bg-white/5 border-white/10 text-white h-10" placeholder="Shift jour" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Début</Label><Input data-testid="shift-start-input" type="time" value={start} onChange={e => setStart(e.target.value)} className="bg-white/5 border-white/10 text-white h-10" /></div>
            <div className="space-y-1.5"><Label className="text-slate-300 text-xs">Fin</Label><Input data-testid="shift-end-input" type="time" value={end} onChange={e => setEnd(e.target.value)} className="bg-white/5 border-white/10 text-white h-10" /></div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Couleur</Label>
            <div className="flex gap-2">
              {COLORS.map(c => (
                <button key={c} type="button" onClick={() => setColor(c)} className={`w-7 h-7 rounded-full ${c} ${color === c ? 'ring-2 ring-white ring-offset-2 ring-offset-[#0B0B0E]' : 'opacity-60 hover:opacity-100'}`} />
              ))}
            </div>
          </div>
          <Button data-testid="shift-save-button" className="w-full bg-indigo-500 hover:bg-indigo-600" onClick={save} disabled={saving}>{saving ? '...' : (shift ? 'Enregistrer' : 'Créer le shift')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
